import { CheckCircleOutline, NotificationsActiveRounded, RadioButtonUnchecked, Visibility, VisibilityOff, WarningRounded } from '@mui/icons-material'
import { Button, IconButton, ToggleButton, ToggleButtonGroup } from '@mui/material'
import React, { useCallback, useEffect, useState, useRef } from 'react'
import AlertUI from '../../generic/AlertUI'
import { fetchErrorLogPage, markAllErrorLogs, markErrorLogs } from './alarmSectionsApi'
import AlarmSectionTabs from './AlarmSectionTabs'
import { DEFAULT_ALARM_SECTION } from './alarmSections'
import SmartMateriaUITable from '../../generic/SmartMateriaUITable'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { alarmDescription } from '../../generic/utilities/alarmDescription'

const AlarmsNotifications = (props) => {
  // Story 162. `splitBySection`: this view asks the backend for ONE section, never both. `sections`: the user
  // can switch between them, so the tabs show. The alarms view is always split -- a user without the
  // automation permission still gets General alarms only -- while the notifications view is not split at all.
  const {
    title,
    state,
    toolbar,
    unChecked,
    sections = false,
    splitBySection = false,
    initialSection = DEFAULT_ALARM_SECTION,
    sectionCounts = {}
  } = props
  const { t_ } = useSmartTranslation()
  const forceLoad = useRef(true)
  const [AlertElement, showAlert] = AlertUI()
  const [selectedNotifications, setSelectedNotifications] = useState(state === 'NOTIFICATIONS')
  const [selectedAlarms, setSelectedAlarms] = useState(state === 'ALARMS')
  const [resetMultipleSelectionValue, setResetMultipleSelectionValue] = useState(false)
  const [section, setSection] = useState(initialSection)
  // '' asks for both, which is what an unsplit view has always done.
  const requestSection = sections || splitBySection ? section : ''

  // The installation's setting can arrive after the first render (frontEndConfig loads after login), so
  // follow it when it changes. It only changes once in practice, so this never fights a manual switch.
  useEffect(() => {
    setSection(initialSection)
  }, [initialSection])

  useEffect(() => {
    if (state === 'ALARMS') {
      setSelectedAlarms(true)
    } else if (state === 'NOTIFICATIONS') {
      setSelectedNotifications(true)
    }
  }, [state])

  const getSelectedLevels = useCallback(() => {
    const levels = []
    if (selectedNotifications) levels.push('INF')
    if (selectedAlarms) levels.push('ERR', 'WAR')
    return levels
  }, [selectedNotifications, selectedAlarms])

  // A catalogue group/type arrives as `{ code }`, or as `{}` when there is none. The code is the
  // stable i18n key (S126).
  const catalogLabel = (entry) => (entry && entry.code ? t_(entry.code) : '')

  // `resolved` carries the three values of AlarmResolution. NOT_APPLICABLE is the resolution of an
  // EVENT (RN25) — inicio/fin de job, i.e. every bell notice — and it has nothing to close, so the
  // cell is left empty instead of claiming the alarm is still pending.
  const renderResolution = (row) => {
    if (row.resolved === 'RESOLVED') {
      return <CheckCircleOutline color='success' />
    }
    if (row.resolved === 'ACTIVE') {
      return <RadioButtonUnchecked color='disabled' />
    }
    return ''
  }

  const columns = [
    {
      field: 'level',
      search: false,
      render: (row) => (
        row.level === 'INF'
          ? <NotificationsActiveRounded color='info' />
          : row.level === 'WAR'
            ? <WarningRounded color='warning' />
            : row.level === 'ERR'
              ? <WarningRounded color='error' />
              : null
      )
    },
    {
      name: t_('Code'),
      field: 'code'
    },
    {
      // Story 204, RN3.7: an alarm Sun composed is translated by its descriptionCode with its data; otherwise
      // story 162, CA13: the cell's PLC errors are translated by their code and everything else is painted as sent.
      name: t_('Description'),
      field: 'description',
      render: (row) => alarmDescription(t_, row)
    },
    {
      name: t_('Creation date'),
      field: 'date',
      type: 'datetime',
      sortOptions: 'asc'
    },
    {
      name: t_('Reading date'),
      field: 'readingDate',
      type: 'datetime'
    },
    {
      // S131 (P8): the catalogue label is translated by its STABLE CODE. `code` and `description` of
      // the alarm above keep being painted raw from the server — that debt is deliberately kept, with
      // the exceptions of the cell's PLC errors (story 162, CA13) and the coded descriptions (story 204, RN3.7).
      // The historic alarms are not classified (RN31/D17), so they arrive with no group and the cell
      // stays empty rather than showing a made-up one.
      name: t_('Group'),
      field: 'group.code',
      render: (row) => catalogLabel(row.group)
    },
    {
      // Empty when the alarm has no type: machine, cell sensors and integrations are classified by
      // rule into their group and are only ever reachable through it (RN5).
      name: t_('Type'),
      field: 'type.code',
      render: (row) => catalogLabel(row.type)
    },
    {
      name: t_('Resolved'),
      field: 'resolved',
      search: false,
      render: (row) => renderResolution(row)
    }
  ]
  const sortOptions = [{ field: 'date', direction: -1 }]

  const updateErrorLogCheckState = useCallback(async (newCheckedState, multipleSelectionValue) => {
    const levels = getSelectedLevels()

    if (multipleSelectionValue.allSelected) {
      await markAllErrorLogs(requestSection, levels, newCheckedState)
    } else {
      const ids = multipleSelectionValue.rows.map(r => r.id)
      await markErrorLogs(ids, newCheckedState)
    }

    showAlert({ title: t_('Updated correctly.'), severity: 'success' })
    setResetMultipleSelectionValue(prev => !prev)
  }, [getSelectedLevels, showAlert, t_, requestSection])

  const actions = {
    custom: [
      {
        contextMenu: false,
        render: (row) => (
          <IconButton
            onClick={() => updateErrorLogCheckState(true, { allSelected: false, rows: [row] })}
            disabled={row.checked}
          >
            <Visibility style={{ color: row.checked ? '#ccc' : '#7FE1A7' }} />
          </IconButton>
        )
      }
    ]
  }

  const multipleSelectionActions = [
    {
      render: (_, index, _pageable, multipleSelectionValue) => (
        <Button
          color='primary' sx={{ borderColor: 'transparent' }} key={'read' + index}
          onClick={() => updateErrorLogCheckState(true, multipleSelectionValue)}
        >
          <Visibility style={{ marginRight: '0.5em', color: '#7FE1A7' }} />{t_('Read')}
        </Button>
      )
    },
    {
      render: (_, index, _pageable, multipleSelectionValue) => (
        <Button
          color='primary' sx={{ borderColor: 'transparent' }} key={'unread' + index}
          onClick={() => updateErrorLogCheckState(false, multipleSelectionValue)}
        >
          <VisibilityOff style={{ marginRight: '0.5em', color: '#7FE1A7' }} />{t_('UnRead')}
        </Button>
      )
    }
  ]

  const fetchData = useCallback(async (pagination) => {
    const levels = getSelectedLevels()
    if (levels.length === 0) return { ...pagination, content: [], totalElements: 0 }

    return await fetchErrorLogPage(requestSection, levels, unChecked, pagination)
      .catch(error => showAlert({ title: t_('Error getting data.'), message: error }))
  }, [showAlert, unChecked, t_, getSelectedLevels, requestSection])

  useEffect(() => {
    forceLoad.current = !forceLoad.current
  }, [selectedNotifications, selectedAlarms, section])

  // A selection made in one section means nothing in the other.
  useEffect(() => {
    setResetMultipleSelectionValue(prev => !prev)
  }, [section])

  const defaultToolbar = {
    render: {
      fetchFunction: () => (
        <ToggleButtonGroup
          value={[selectedNotifications ? 'NOTIFICATIONS' : '', selectedAlarms ? 'ALARMS' : '']}
          onChange={(_, newFormats) => {
            setSelectedNotifications(newFormats.includes('NOTIFICATIONS'))
            setSelectedAlarms(newFormats.includes('ALARMS'))
          }}
        >
          <ToggleButton value='NOTIFICATIONS' color='info'><NotificationsActiveRounded />{t_('Notifications')}</ToggleButton>
          <ToggleButton value='ALARMS' color='warning'><WarningRounded />{t_('Alarms')}</ToggleButton>
        </ToggleButtonGroup>
      )
    }
  }

  return (
    <>
      {AlertElement}
      {sections && <AlarmSectionTabs value={section} onChange={setSection} counts={sectionCounts} />}
      <SmartMateriaUITable
        tableTuneUniqueKey='alarmNotification'
        columns={columns}
        title={title}
        dataFetch={fetchData}
        actions={actions}
        toolbar={toolbar ?? defaultToolbar}
        sortOptions={sortOptions}
        forceLoad={forceLoad.current}
        multipleSelection
        multipleSelectionActions={multipleSelectionActions}
        deleteMultipleSelectionDisabled
        resetMultipleSelectionValue={resetMultipleSelectionValue}
        pageable
      />
    </>
  )
}

export default AlarmsNotifications
